import * as React from 'react';
import * as _ from 'lodash';
import {useState, useRef, useEffect} from 'react';

import classNames from 'classnames';

import {Viewer} from '../state/viewer/types';
import * as ReportTypes from '../state/reports/types';
import LegacyWBIcon from './elements/LegacyWBIcon';
import {Modal, Popup, Button, Input, Message} from 'semantic-ui-react';

import {useApolloClient, useSelector} from '../state/hooks';
import {useReportPageQuery} from '../state/graphql/reportPageQuery';
import * as Urls from '../util/urls';

import {FeaturedReportCard} from './FeaturedGallery';
import {fuzzyMatchRegex} from '../util/fuzzyMatch';

import {
  PROJECT_ACCESSES_QUERY,
  useFeaturedReportsQuery,
} from '../state/graphql/reportAdminBar';
import {useUpdateFeaturedReportsMutation} from '../generated/graphql';
import {PartialSome} from '../types/base';
import {capitalizeFirst} from '../util/string';
import {emptyReportData, FeaturedReportsSpec} from '../util/reportGallery';
import {parseNameAndIDFromURL} from '../util/url';
import {toPublishedReportURL} from '../util/urls';
import {ReportObjSchema} from '../state/views/report/types';
import {useReportProjects} from '../util/report';
import {accessIsPrivate} from '../util/permissions';
import makeComp from '../util/profiler';
import {FeaturedReport, useGalleryTagSections} from '../util/gallery';
import {ReportAuthor} from '../util/section';

const reportURLHasID = (url: string, id: string): boolean => {
  const {id: reportID} = parseNameAndIDFromURL(url);
  return reportID === id;
};

interface ReportAdminBarProps {
  viewRef: ReportTypes.ReportViewRef;
  viewer?: Viewer;
  entityName: string;
  projectName: string;
  editAccess?: boolean;
  publishedWork?: boolean;
}
const adminColor = '#884BF2';

export const ReportAdminBar = makeComp(
  (props: ReportAdminBarProps) => {
    const tagSections = useGalleryTagSections();
    const featuredReportsResult = useFeaturedReportsQuery({});

    const view = useSelector(state => state.views.views[props.viewRef.id]);
    const viewPart = useSelector(state =>
      view?.partRef != null
        ? state.views.parts[view.partRef.type as ReportObjSchema['type']][
            view.partRef.id
          ]
        : null
    );

    const {entityName, projectName} = props;

    // TODO(axel): DEPRECATED. REMOVE.
    const author =
      viewPart?.authors != null && viewPart.authors.length > 0
        ? viewPart.authors.map(a => a.name).join(', ')
        : view.user.name ?? null;

    const authors: string[] =
      viewPart?.authors != null && viewPart.authors.length > 0
        ? viewPart.authors.map(a => a.username)
        : [view.user.username];

    const url =
      view.id != null
        ? Urls.reportView({
            entityName: view.project.entityName,
            projectName: view.project.name,
            reportID: view.id,
            reportName: view.displayName,
          })
        : null;

    const EMPTY_DRAFT = {
      tags: [] as string[],
    };
    const [reportCardDraft, updateReportCardDraft] = useState<{
      [key: string]: any;
    }>();

    const reportQuery = useReportPageQuery({
      entityName,
      projectName,
    });

    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const [popupOpen, setPopupOpen] = React.useState<boolean>(false);
    const [party, setParty] = React.useState<boolean>(false);
    const [addingToGallery, setAddingToGallery] = React.useState(false);
    const [removingFromGallery, setRemovingFromGallery] = React.useState(false);

    const PARTY_DELAY = 40;
    const PARTY_DURATION = 2200;
    const throwParty = () =>
      setTimeout(() => {
        setParty(true);
        setTimeout(() => {
          setParty(false);
        }, PARTY_DURATION);
      }, PARTY_DELAY);

    const openModal = () => {
      setModalOpen(true);

      // HAX: No good way to set a style on Semantic's dimmer which wraps the modal.
      setTimeout(() => {
        const dimmer = document.querySelector('.ui.dimmer') as HTMLDivElement;
        dimmer.style.overflow = 'auto';
      });
    };

    const featuredReportsViewID = !featuredReportsResult.loading
      ? featuredReportsResult.featuredReports?.id
      : null;
    const featuredReportsSpec = !featuredReportsResult.loading
      ? featuredReportsResult.featuredReports?.spec
      : null;

    const featuredReports: FeaturedReportsSpec = React.useMemo(
      () =>
        featuredReportsSpec != null
          ? JSON.parse(featuredReportsSpec)
          : emptyReportData,

      [featuredReportsSpec]
    );

    const [updateFeaturedReports] = useUpdateFeaturedReportsMutation();

    useEffect(() => {
      if (reportCardDraft === undefined && !featuredReportsResult.loading) {
        const serverReport =
          featuredReports.reports.find(
            r => view.id != null && reportURLHasID(r.link, view.id)
          ) ?? EMPTY_DRAFT;

        updateReportCardDraft(serverReport);
      }
      // We only want this hook to run once after results load
      /* eslint-disable react-hooks/exhaustive-deps */
    }, [featuredReportsResult.loading]);

    const reportFromGallery = featuredReports.reports.find(
      r => view.id != null && reportURLHasID(r.link, view.id)
    );

    const inGallery = reportFromGallery != null;

    const client = useApolloClient();
    const projectSpecifiers = useReportProjects(props.viewRef);
    const hasPrivateRunSetRef = useRef(false);
    useEffect(() => {
      const projectAccessPromises = projectSpecifiers.map(ps =>
        client.query({
          query: PROJECT_ACCESSES_QUERY,
          variables: {name: ps.projectName, entityName: ps.entityName},
        })
      );
      (async () => {
        try {
          const results = await Promise.all(projectAccessPromises);
          hasPrivateRunSetRef.current = results.some(r => {
            const acc = r?.data?.project?.access;
            if (acc == null) {
              return true;
            }
            return accessIsPrivate(acc);
          });
        } catch (err) {
          console.error('error loading project accesses', err);
          console.log('project specifiers', projectSpecifiers);
        }
      })();
    }, [client, projectSpecifiers]);

    if (author == null || url == null) {
      return <></>;
    }

    if (reportQuery.loading === true) {
      return <></>;
    }

    if (featuredReportsResult.loading === true) {
      return <></>;
    }

    const staticReportCardData = {
      id: view.id!,
      link: toPublishedReportURL(url),
      imageURL: view.previewUrl,
      title: view.displayName,
      author,
      authors,
      description: view.description,
    };

    const currentReportCard: PartialSome<FeaturedReport, 'imageURL'> = {
      ...staticReportCardData,
      tags: reportCardDraft?.tags ?? [],
      addedAt: reportCardDraft?.addedAt,
    };

    const setTags = (tags: string[]) => {
      updateReportCardDraft({tags});
    };

    const addToGallery = async () => {
      if (hasPrivateRunSetRef.current) {
        alert(
          'This report contains private run set(s). You must publish it to add it to the gallery.'
        );
        return;
      }

      const fullCard =
        currentReportCard.imageURL != null &&
        (currentReportCard as FeaturedReport);
      if (!fullCard) {
        alert('Report must have cover image set to be added to the gallery');
        return;
      }

      setAddingToGallery(true);

      const {
        data: {featuredReports: updatedFeaturedReportsResult},
      } = await featuredReportsResult.refetch();
      const updatedFeaturedReports: FeaturedReportsSpec =
        updatedFeaturedReportsResult?.spec != null
          ? JSON.parse(updatedFeaturedReportsResult.spec)
          : featuredReports;

      const now = Date.now();
      if (fullCard.addedAt == null) {
        fullCard.addedAt = now;
      }

      let newReportList: FeaturedReport[];
      if (inGallery) {
        const reportIdx = updatedFeaturedReports.reports.findIndex(
          r => view.id != null && reportURLHasID(r.link, view.id)
        );
        newReportList = _.cloneDeep(updatedFeaturedReports.reports);
        newReportList = [
          ...newReportList.slice(0, reportIdx),
          fullCard,
          ...newReportList.slice(reportIdx + 1).filter(r => {
            if (view.id == null) {
              return true;
            }
            return !reportURLHasID(r.link, view.id);
          }),
        ];
      } else {
        newReportList = [fullCard, ...updatedFeaturedReports.reports];
      }

      try {
        await updateFeaturedReports({
          variables: {
            id: featuredReportsViewID,
            spec: JSON.stringify({
              ...updatedFeaturedReports,
              reports: newReportList,
            }),
          },
        });
        throwParty();
        updateReportCardDraft(fullCard);
        await featuredReportsResult.refetch();
      } catch {
        alert('Failed to save');
      } finally {
        setAddingToGallery(false);
      }
    };

    const removeFromGallery = async () => {
      setRemovingFromGallery(true);
      const {
        data: {featuredReports: updatedFeaturedReportsResult},
      } = await featuredReportsResult.refetch();
      const updatedFeaturedReports: FeaturedReportsSpec =
        updatedFeaturedReportsResult?.spec != null
          ? JSON.parse(updatedFeaturedReportsResult.spec)
          : featuredReports;

      const newReportList = _.filter(updatedFeaturedReports.reports, r => {
        if (view.id == null) {
          return true;
        }
        return !reportURLHasID(r.link, view.id);
      });

      try {
        await updateFeaturedReports({
          variables: {
            id: featuredReportsViewID,
            spec: JSON.stringify({
              ...updatedFeaturedReports,
              reports: newReportList,
            }),
          },
        });
        throwParty();
        featuredReportsResult.refetch();
      } catch {
        alert('Failed to remove');
      } finally {
        setRemovingFromGallery(false);
      }
    };

    const allTags: string[] = [];
    for (const section of tagSections) {
      for (const t of section.tags) {
        allTags.push(t.name);
      }
    }

    const access = reportQuery.project.access;
    const featureable = access === 'USER_READ' || access === 'USER_WRITE';

    let authorInfo: ReportAuthor[];
    if (viewPart != null && viewPart.authors) {
      authorInfo = viewPart.authors.map(a => ({
        name: a.name ?? '',
        username: a.username,
      }));
    } else {
      authorInfo = [
        {
          name: view.user.name ?? '',
          username: view.user.username,
        },
      ];
    }

    return (
      <div
        style={{
          width: '100%',
          height: 52,
          backgroundColor: '#bd9df4',
          display: 'flex',
          alignItems: 'center',
        }}>
        <Popup
          basic
          open={popupOpen}
          onOpen={() => {
            setPopupOpen(true);
          }}
          onClose={() => {
            setPopupOpen(false);
          }}
          trigger={
            <LegacyWBIcon
              style={{
                fontSize: '2.0em',
                cursor: 'pointer',
                color: adminColor,
                position: 'absolute',
                right: 15,
              }}
              name="bowtie"></LegacyWBIcon>
          }
          on="click">
          <Popup.Header
            style={{
              color: adminColor,
              textTransform: 'uppercase',
              alignItems: 'center',
              display: 'flex',
            }}>
            <LegacyWBIcon
              style={{
                fontSize: '1.15em',
              }}
              name="bowtie"></LegacyWBIcon>
            <span style={{fontSize: 12}}>Admin Only</span>
          </Popup.Header>
          <div
            onClick={() => {
              setPopupOpen(false);
              openModal();
            }}
            style={{cursor: 'pointer'}}>
            Add report to gallery
          </div>
        </Popup>

        <Modal
          open={modalOpen}
          onOpen={() => {
            openModal();
          }}
          onClose={() => setModalOpen(false)}>
          <Modal.Header
            style={{color: adminColor, display: 'flex', alignItems: 'center'}}>
            <LegacyWBIcon
              style={{color: adminColor}}
              size="big"
              name="bowtie"></LegacyWBIcon>
            Admin: Add to gallery
            <span
              style={{
                transition: 'all 0.2s',
                opacity: party ? 1 : 0,
                position: 'absolute',
                right: 20,
              }}>
              Successful update!
              <span aria-label="cheers" role="img">
                🥂
              </span>
              <span aria-label="growth" role="img">
                🌱
              </span>
            </span>
          </Modal.Header>
          {featureable ? (
            <>
              <Modal.Content>
                Here's what the report will look like in the gallery. Add tags
                from the list to categorize this report.
                {currentReportCard.imageURL != null ? (
                  <div style={{maxWidth: 600, margin: 'auto'}}>
                    <FeaturedReportCard
                      id={parseNameAndIDFromURL(currentReportCard.link).id}
                      author={currentReportCard.author}
                      description={currentReportCard.description}
                      imageURL={currentReportCard.imageURL}
                      link={currentReportCard.link}
                      title={currentReportCard.title}
                      tags={currentReportCard.tags}
                      authorInfo={authorInfo}
                    />
                  </div>
                ) : (
                  <Message style={{maginTop: 20}} error={true}>
                    No cover url set
                  </Message>
                )}
                <TagPicker
                  availableTags={allTags}
                  setTags={setTags}
                  currentTags={currentReportCard.tags}></TagPicker>
              </Modal.Content>
              <Modal.Actions>
                <Button
                  disabled={
                    !inGallery || removingFromGallery || addingToGallery
                  }
                  loading={removingFromGallery}
                  style={{
                    backgroundColor: inGallery ? adminColor : null,
                    color: inGallery ? 'white' : null,
                    position: 'absolute',
                    left: 10,
                  }}
                  onClick={() => {
                    removeFromGallery();
                  }}>
                  Remove from gallery
                </Button>
                <Button
                  // style={{backgroundColor: adminColor, color: 'white'}}
                  onClick={() => {
                    setModalOpen(false);
                  }}>
                  Cancel
                </Button>
                <Button
                  disabled={addingToGallery || removingFromGallery}
                  loading={addingToGallery}
                  style={{backgroundColor: adminColor, color: 'white'}}
                  onClick={addToGallery}>
                  {inGallery ? 'Update gallery card' : 'Add to gallery'}
                </Button>
              </Modal.Actions>
            </>
          ) : (
            <>
              <Modal.Content>
                This project is private — make it public to add this report to
                the gallery
              </Modal.Content>

              <Modal.Actions>
                <Button
                  style={{backgroundColor: adminColor, color: 'white'}}
                  onClick={() => {
                    setModalOpen(false);
                  }}>
                  Okay
                </Button>
              </Modal.Actions>
            </>
          )}
        </Modal>
      </div>
    );
  },
  {id: 'ReportAdminBar'}
);

const TagPicker = makeComp(
  (props: {
    setTags: (tags: string[]) => void;
    currentTags: string[];
    availableTags: string[];
  }) => {
    const {availableTags} = props;
    const [showDropdown, setShowDropdown] = useState(false);
    const [query, setQuery] = useState('');
    const inputRef = useRef<Input | null>(null);

    const matchedTags = availableTags.filter(t =>
      t.match(fuzzyMatchRegex(query))
    );

    const setTags = (tags: string[]) => {
      props.setTags(_.uniq(tags));
    };

    return (
      <div
        style={{
          width: '100%',
          marginTop: 10,
        }}
        className={classNames('report-header-edit__author-adder', 'active')}>
        <div
          onClick={() => {
            if (inputRef.current != null) {
              inputRef.current.focus();
            }
          }}
          className="author-adder-fake-input">
          {props.currentTags.map(tag => (
            <div key={tag} className="author-adder-added">
              {tag}
              <LegacyWBIcon
                name="close"
                className="author-adder-remove"
                onClick={() => {
                  setTags(props.currentTags.filter(v => v !== tag));
                }}
              />
            </div>
          ))}
          <Input
            ref={el => (inputRef.current = el)}
            value={query}
            onChange={(e, {value}) => setQuery(value)}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setShowDropdown(false)}
            placeholder={'Select Tags'}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter') {
                setTags(props.currentTags.concat([capitalizeFirst(query)]));
                setQuery('');
              }
            }}
          />
        </div>
        <div
          style={{zIndex: 2}}
          className={classNames('author-adder-dropdown', {
            active: showDropdown && availableTags.length > 0,
          })}>
          {matchedTags.map(tag => (
            <div
              key={tag}
              className="author-adder-dropdown-item"
              onClick={() => {
                setTags(props.currentTags.concat([tag]));
                setQuery('');
              }}>
              {tag}
            </div>
          ))}
        </div>
      </div>
    );
  },
  {id: 'TagPicker'}
);
